/* eslint-disable */
// Session 55 · Alpha Launch Phase 2 — Email QA Console.
// Admin-only tool that lets Cam:
//  · see the PASS/FAIL matrix across every transactional template,
//  · preview each rendered HTML in desktop + mobile viewports (iframe),
//  · fire a test copy to any inbox so he can eyeball Gmail / Apple Mail /
//    Outlook rendering himself in minutes.

import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const CATEGORY_COLORS = {
  onboarding: "#B8913A",
  auth: "#5B7C99",
  booking: "#1B2340",
  owner: "#6E4C2F",
  community: "#83B26E",
};

export default function AdminEmails() {
  const { user, loading } = useAuth();
  const [audit, setAudit] = useState(null);
  const [selected, setSelected] = useState(null);
  const [rendered, setRendered] = useState(null);
  const [viewport, setViewport] = useState("desktop"); // desktop | mobile
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [err, setErr] = useState("");
  const [testLog, setTestLog] = useState([]);

  const loadAudit = async () => {
    try {
      const r = await api.get("/admin/emails/audit");
      setAudit(r.data);
      if (!selected && r.data.checks?.length) setSelected(r.data.checks[0].name);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load audit"); }
  };

  const loadLog = async () => {
    try {
      const r = await api.get("/admin/emails/test-log");
      setTestLog(r.data.log || []);
    } catch (e) { /* nbd */ }
  };

  useEffect(() => { if (user?.role === "admin") { loadAudit(); loadLog(); } }, [user]);

  useEffect(() => {
    if (!selected) return;
    setRendered(null);
    api.get(`/admin/emails/render/${selected}`)
      .then(r => setRendered(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Render failed"));
  }, [selected]);

  const sendTest = async () => {
    if (!selected || !testTo) return;
    setBusy(true); setErr(""); setFlash("");
    try {
      const r = await api.post("/admin/emails/test-send", {
        template: selected, to: testTo,
      });
      setFlash(`Sent [TEST] ${rendered?.subject || selected} to ${testTo}`);
      loadLog();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const passRate = useMemo(() => {
    if (!audit) return 0;
    return audit.total > 0 ? Math.round((audit.pass / audit.total) * 100) : 0;
  }, [audit]);

  if (loading) return <div style={{padding:40}}>Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  return (
    <div style={{padding:"32px 40px", maxWidth:1300, margin:"0 auto", fontFamily:"Jost, sans-serif", color:"#1B2340"}} data-testid="admin-emails-page">
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20}}>
        <div>
          <span className="m-eyebrow" style={{color:"var(--gold)"}}>Admin · Email QA</span>
          <h1 style={{fontFamily:"Cormorant Garamond, serif", fontSize:34, margin:"4px 0 0", fontWeight:500}}>Transactional Email Audit</h1>
        </div>
        <Link to="/admin" data-testid="admin-emails-back" style={{color:"#B8913A"}}>← Admin</Link>
      </div>

      {flash && <div style={{background:"#F0F7EE", border:"1px solid #83B26E", padding:"10px 14px", borderRadius:8, marginBottom:16, fontSize:13}} data-testid="admin-emails-flash">{flash}</div>}
      {err && <div style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginBottom:16, fontSize:13}} data-testid="admin-emails-error">{err}</div>}

      {audit && (
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:12, marginBottom:24}}>
          <Kpi label="Pass rate" value={`${passRate}%`} testid="kpi-passrate" accent={passRate >= 95 ? "#83B26E" : "#B8913A"} />
          <Kpi label="Templates" value={audit.total} testid="kpi-total" />
          <Kpi label="Passing" value={audit.pass} testid="kpi-pass" accent="#83B26E" />
          <Kpi label="Warnings" value={audit.warn} testid="kpi-warn" accent="#B8913A" />
          <Kpi label="Failing" value={audit.fail} testid="kpi-fail" accent={audit.fail > 0 ? "#C05353" : "#83B26E"} />
          <Kpi label="From" value={audit.from_email} testid="kpi-from" small />
          <Kpi label="Reply-To" value={audit.reply_to || "(none)"} testid="kpi-replyto" small />
          <Kpi label="Resend" value={audit.resend_configured ? "configured" : "MISSING"} testid="kpi-resend" accent={audit.resend_configured ? "#83B26E" : "#C05353"} />
        </div>
      )}

      <div style={{display:"grid", gridTemplateColumns:"340px 1fr", gap:24, alignItems:"start"}}>
        {/* LEFT: template list */}
        <aside style={{maxHeight:"calc(100vh - 260px)", overflowY:"auto", border:"1px solid rgba(184,145,58,0.2)", borderRadius:12, padding:12, background:"rgba(184,145,58,0.03)"}} data-testid="admin-emails-list">
          {audit?.checks?.map((c) => {
            const cat = c.category || "other";
            const dot = CATEGORY_COLORS[cat] || "#666";
            const active = selected === c.name;
            const statusColor = c.status === "pass" ? "#83B26E" : c.status === "warn" ? "#B8913A" : "#C05353";
            return (
              <button
                key={c.name}
                type="button"
                onClick={() => setSelected(c.name)}
                data-testid={`email-tpl-${c.name}`}
                style={{
                  width:"100%", textAlign:"left", padding:"10px 12px",
                  background: active ? "rgba(184,145,58,0.14)" : "transparent",
                  border: active ? "1px solid rgba(184,145,58,0.4)" : "1px solid transparent",
                  borderRadius:8, marginBottom:4, cursor:"pointer",
                  color:"#1B2340", display:"flex", gap:10, alignItems:"flex-start",
                }}>
                <span style={{width:8, height:8, borderRadius:"50%", background:statusColor, marginTop:6, flexShrink:0}} />
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:500, whiteSpace:"nowrap", textOverflow:"ellipsis", overflow:"hidden"}}>{c.name}</div>
                  <div style={{fontSize:11, color:"rgba(27,35,64,0.6)", marginTop:2}}>
                    <span style={{color:dot, fontWeight:600}}>{cat}</span> · {c.subject}
                  </div>
                </div>
              </button>
            );
          })}
        </aside>

        {/* RIGHT: preview + tools */}
        <main data-testid="admin-emails-preview">
          {rendered && (
            <>
              <div style={{background:"#fff", border:"1px solid rgba(184,145,58,0.25)", borderRadius:12, padding:18, marginBottom:16}}>
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12}}>
                  <div>
                    <div style={{fontSize:11, letterSpacing:"0.14em", color:"rgba(27,35,64,0.6)", textTransform:"uppercase"}}>Subject</div>
                    <div style={{fontSize:16, fontWeight:500, marginTop:2}} data-testid="email-subject">{rendered.subject}</div>
                    <div style={{fontSize:12, color:"rgba(27,35,64,0.6)", marginTop:4}}>
                      From <strong>{rendered.from_email}</strong>{rendered.reply_to && <> · Reply-To <strong>{rendered.reply_to}</strong></>}
                    </div>
                  </div>
                  <div style={{display:"flex", gap:6}} data-testid="email-viewport-switch">
                    <button type="button" onClick={() => setViewport("desktop")}
                      data-testid="vp-desktop"
                      style={pill(viewport === "desktop")}>Desktop</button>
                    <button type="button" onClick={() => setViewport("mobile")}
                      data-testid="vp-mobile"
                      style={pill(viewport === "mobile")}>Mobile</button>
                  </div>
                </div>

                <div style={{display:"flex", gap:8, alignItems:"stretch", marginTop:14, flexWrap:"wrap"}} data-testid="email-testsend-row">
                  <input
                    type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                    placeholder="you@gmail.com — send a test copy"
                    data-testid="email-testsend-input"
                    style={{flex:1, minWidth:220, padding:"9px 12px", border:"1px solid rgba(27,35,64,0.2)", borderRadius:8, fontSize:13}}
                  />
                  <button type="button" onClick={sendTest} disabled={busy || !testTo}
                    data-testid="email-testsend-btn"
                    style={{padding:"9px 18px", background:"#B8913A", color:"#FAF8F3", border:"none", borderRadius:8, cursor:"pointer", fontSize:13}}>
                    {busy ? "Sending…" : "Send test"}
                  </button>
                </div>
                <div style={{fontSize:11, color:"rgba(27,35,64,0.55)", marginTop:6}}>
                  Deliver a real copy to any inbox to verify Gmail / Apple Mail / Outlook rendering.
                </div>
              </div>

              <div style={{
                background: viewport === "desktop" ? "#f0ede5" : "#1B2340",
                padding: viewport === "desktop" ? 20 : "24px 12px",
                borderRadius:12, display:"flex", justifyContent:"center", overflow:"hidden",
              }} data-testid="email-preview-frame-wrap">
                <iframe
                  key={selected + viewport}
                  data-testid="email-preview-iframe"
                  title={`preview-${selected}`}
                  srcDoc={rendered.html}
                  style={{
                    width: viewport === "desktop" ? "100%" : 375,
                    maxWidth: viewport === "desktop" ? 720 : 375,
                    height: viewport === "desktop" ? 800 : 720,
                    border: viewport === "mobile" ? "6px solid #0d1226" : "1px solid rgba(27,35,64,0.1)",
                    borderRadius: viewport === "mobile" ? 24 : 6,
                    background:"white",
                  }}
                />
              </div>

              {audit && (
                <SignalsTable check={audit.checks.find(c => c.name === selected)} />
              )}
            </>
          )}

          {testLog?.length > 0 && (
            <div style={{marginTop:24}} data-testid="email-testlog">
              <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"rgba(27,35,64,0.6)", marginBottom:8}}>Recent test sends</div>
              <table style={{width:"100%", fontSize:12, borderCollapse:"collapse"}}>
                <thead><tr style={{textAlign:"left", borderBottom:"1px solid rgba(184,145,58,0.25)"}}>
                  <th style={th}>When</th><th style={th}>Template</th><th style={th}>To</th><th style={th}>By</th>
                </tr></thead>
                <tbody>
                  {testLog.slice(0, 15).map((r, i) => (
                    <tr key={i} style={{borderBottom:"1px solid rgba(27,35,64,0.06)"}}>
                      <td style={td}>{r.sent_at?.slice(0,19).replace("T"," ")}</td>
                      <td style={td}>{r.template}</td>
                      <td style={td}>{r.to}</td>
                      <td style={td}>{r.sent_by_email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

const pill = (active) => ({
  padding:"6px 14px", border: active ? "1px solid #B8913A" : "1px solid rgba(27,35,64,0.2)",
  background: active ? "rgba(184,145,58,0.14)" : "#fff",
  color: active ? "#B8913A" : "#1B2340",
  borderRadius:999, cursor:"pointer", fontSize:12,
});

const th = {padding:"6px 6px", fontSize:11, color:"rgba(27,35,64,0.6)"};
const td = {padding:"5px 6px", color:"#1B2340"};

function Kpi({label, value, testid, accent, small}) {
  return (
    <div data-testid={testid} style={{
      border:"1px solid rgba(184,145,58,0.25)", borderRadius:12, padding:"12px 14px",
      background:"rgba(184,145,58,0.04)",
    }}>
      <div style={{fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase", color:"rgba(27,35,64,0.6)"}}>{label}</div>
      <div style={{fontSize: small ? 12 : 22, fontFamily: small ? "Jost" : "Cormorant Garamond, serif", marginTop:4, color: accent || "#1B2340", wordBreak:"break-all"}}>{value}</div>
    </div>
  );
}

function SignalsTable({check}) {
  if (!check) return null;
  const rows = Object.entries(check.signals || {});
  return (
    <div style={{marginTop:16, background:"#fff", border:"1px solid rgba(184,145,58,0.25)", borderRadius:12, padding:16}} data-testid="email-signals-table">
      <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"rgba(27,35,64,0.6)", marginBottom:10}}>Signal checks</div>
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:8}}>
        {rows.map(([k, v]) => (
          <div key={k} style={{display:"flex", alignItems:"center", gap:8, fontSize:13}}>
            <span style={{width:8, height:8, borderRadius:"50%", background: v ? "#83B26E" : "#C05353"}} />
            <span style={{color:"rgba(27,35,64,0.85)"}}>{k.replace(/_/g," ")}</span>
          </div>
        ))}
      </div>
      <div style={{marginTop:12, fontSize:12, color:"rgba(27,35,64,0.6)"}}>
        <strong>Trigger:</strong> {check.trigger}
      </div>
    </div>
  );
}
